/**
 * 海域図（農林漁区番号グリッド）。担当: G4（ポータル・S-72・S-73 で共用）
 *
 * kintone 版 `apps/portal/OceanAreaMap.tsx` の再現：背景画像（/images/sea-area-map.png）の上に
 * 18 行 × 30 列のグリッドを重ね、農林漁区番号ごとの件数（隻数）を色塗り・数字で表示する。
 *   青＝事前連絡・実績（調査海域）／ピンク＝視認／緑＝漁具
 * グリッドの位置は背景画像（1055×737px）上の格子（左 38px・上 31.5px・幅 996px・高さ 676px）に合わせている。
 */
import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cx } from '@/lib/cx';
import './sea-area-map.css';

export type SeaMapTone = 'blue' | 'pink' | 'green';

export interface SeaAreaGrid {
  rows: number;
  cols: number;
  imageUrl: string;
  cells: { rowNo: number; colNo: number; zoneNo: number }[];
}

export interface ZoneCount {
  zoneNo: number;
  count: number;
}

export function useSeaAreaGrid() {
  return useQuery({
    queryKey: ['sea-map', 'grid'],
    queryFn: ({ signal }) => api.get<SeaAreaGrid>('/sea-map/grid', undefined, { signal }),
    staleTime: Infinity,
  });
}

export interface SeaAreaMapProps {
  counts: ReadonlyArray<ZoneCount>;
  tone?: SeaMapTone;
  /** true＝色塗り＋件数、false＝色塗りのみ（kintone「隻数表示する／色塗りのみ」） */
  showCounts?: boolean;
  /** 選択中の農林漁区番号（番号条件の選択表示） */
  selected?: ReadonlyArray<number>;
  /** セルクリック（指定すると各セルがボタンになる） */
  onCellClick?: (zoneNo: number) => void;
  /** 件数の単位（ツールチップ用。既定「隻」） */
  unit?: string;
  /** 地図の下に表示する凡例 */
  legend?: ReactNode;
  /** 各マスに農林漁区番号を薄く表示する（既定 true。小さく表示する場合は false） */
  zoneNumbers?: boolean;
  /** 地図の表示幅（px。既定は親の幅いっぱい） */
  maxWidth?: number;
  className?: string;
  'aria-label'?: string;
}

export function SeaAreaMap({
  counts,
  tone = 'blue',
  showCounts = true,
  selected,
  onCellClick,
  unit = '隻',
  legend,
  zoneNumbers = true,
  maxWidth,
  className,
  'aria-label': ariaLabel = '海域図',
}: SeaAreaMapProps) {
  const { data: grid } = useSeaAreaGrid();
  const countMap = useMemo(() => new Map(counts.map((c) => [c.zoneNo, c.count])), [counts]);
  const selectedSet = useMemo(() => new Set(selected ?? []), [selected]);
  const total = counts.reduce((s, c) => s + c.count, 0);
  const rows = grid?.rows ?? 18;
  const cols = grid?.cols ?? 30;

  return (
    <figure className={cx('bs-seamap', `bs-seamap--${tone}`, className)} style={maxWidth ? { maxWidth } : undefined}>
      <div
        className="bs-seamap__canvas"
        style={{ backgroundImage: `url("${grid?.imageUrl ?? '/images/sea-area-map.png'}")` }}
        role={onCellClick ? 'group' : 'img'}
        aria-label={`${ariaLabel}（該当 ${counts.length} 区画・延べ ${total}${unit}）`}
      >
        <div className="bs-seamap__grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}>
          {grid?.cells.map((cell) => {
            const n = countMap.get(cell.zoneNo) ?? 0;
            const hit = n > 0;
            const isSel = selectedSet.has(cell.zoneNo);
            const title = `農林漁区 ${cell.zoneNo}${hit ? `：${n}${unit}` : ''}`;
            const style = { gridRow: cell.rowNo + 1, gridColumn: cell.colNo + 1 };
            // kintone 版と同様に農林漁区番号を薄く表示し、件数は太字で重ねる
            const content = (
              <>
                {zoneNumbers && (
                  <span className="bs-seamap__no bs-num" aria-hidden>
                    {cell.zoneNo}
                  </span>
                )}
                {hit && showCounts && <span className="bs-seamap__count bs-num">{n}</span>}
              </>
            );
            return onCellClick ? (
              <button
                key={`${cell.rowNo}-${cell.colNo}`}
                type="button"
                className={cx('bs-seamap__cell', hit && 'is-hit', isSel && 'is-selected')}
                style={style}
                title={title}
                aria-label={title}
                aria-pressed={isSel}
                data-zone={cell.zoneNo}
                onClick={() => onCellClick(cell.zoneNo)}
              >
                {content}
              </button>
            ) : (
              <span
                key={`${cell.rowNo}-${cell.colNo}`}
                className={cx('bs-seamap__cell', hit && 'is-hit', isSel && 'is-selected')}
                style={style}
                title={title}
                data-zone={cell.zoneNo}
              >
                {content}
              </span>
            );
          })}
        </div>
      </div>
      {legend && <figcaption className="bs-seamap__legend">{legend}</figcaption>}
    </figure>
  );
}

/** 凡例（色見本＋説明） */
export function SeaMapLegend({ tone, children }: { tone: SeaMapTone; children: ReactNode }) {
  return (
    <span className="bs-seamap-legend">
      <span className={cx('bs-seamap-legend__swatch', `bs-seamap--${tone}`)} aria-hidden />
      {children}
    </span>
  );
}
